<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRoute } from '#app';

const route = useRoute();
const slug = route.params.slug;
const siteUrl = 'https://gefestai.com'; // Замени на свой домен или бери из useRuntimeConfig()

// 1. SSR Запрос данных статьи
const { data: article, pending, error } = await useFetch(`${siteUrl}/api/articles/${slug}`, {
    key: `article-${slug}`,
    transform: (res) => res.data || res
});

if (error.value || !article.value) {
    throw createError({ statusCode: 404, statusMessage: 'Статья не найдена', fatal: true });
}

// 2. SEO Оптимизация + Canonical
useSeoMeta({
    title: article.value.meta_title || article.value.title,
    description: article.value.meta_description,
    ogTitle: article.value.meta_title || article.value.title,
    ogDescription: article.value.meta_description,
    ogImage: article.value.cover_image,
    twitterCard: 'summary_large_image',
});

useHead({
    link: [
        { rel: 'canonical', href: `${siteUrl}${route.path}` }
    ]
});

// 3. Форматирование даты
const formattedDate = computed(() => {
    if (!article.value?.published_at) return '';
    return new Date(article.value.published_at).toLocaleDateString('ru-RU', {
        day: 'numeric', month: 'long', year: 'numeric'
    });
});

// 4. Парсер контента: разбиваем текст по маркерам [IMAGE_BLOCK_N]
const articleBlocks = computed(() => {
    if (!article.value?.content_html) return [];

    // 1. Разбиваем текст по маркерам [IMAGE_BLOCK_1], [IMAGE_BLOCK_2] и т.д.
    const rawParts = article.value.content_html.split(/(\[IMAGE_BLOCK_\d+\])/g);

    return rawParts.map(part => {
        const cleanPart = part.trim();
        // Извлекаем номер из маркера [IMAGE_BLOCK_1] -> 1
        const isMarker = cleanPart.match(/^\[IMAGE_BLOCK_(\d+)\]$/);

        if (isMarker) {
            const index = parseInt(isMarker[1]) - 1; // Превращаем в индекс массива (1 -> 0)

            // Берем картинку из пришедшего массива images по этому индексу
            const imageData = article.value.images && article.value.images[index+1];

            if (imageData) {
                return { type: 'image', data: imageData };
            }
            return null; // Если картинки под таким номером нет в базе
        }

        return part !== '' ? { type: 'text', data: part } : null;
    }).filter(Boolean);
});

// 5. МОКИ (Примеры данных, пока нет API)
const mockTags = ref(['Вайбкодинг', 'Автоматизация', 'SaaS', 'Нейросети', 'Nuxt 3']);



const relatedArticles = ref([]);
const relatedPage = ref(1);
const relatedLastPage = ref(1);
const pendingRelated = ref(false);

// SSR-запрос для первой страницы похожих статей
// Предполагается, что Laravel API находится по адресу /api/articles/{slug}/related
const { data: initialRelated } = await useFetch(`${siteUrl}/api/articles/${slug}/related`, {
    key: `related-${slug}-page-1`,
    query: { page: 1, per_page: 4 },
});

// Инициализируем данные, если они пришли от Laravel
if (initialRelated.value) {
    // Laravel paginate() оборачивает массив элементов в ключ 'data'
    relatedArticles.value = initialRelated.value.data || [];
    relatedLastPage.value = initialRelated.value.last_page || 1;
}

// Функция подгрузки следующих страниц (выполняется уже на клиенте)
const loadMoreRelated = async () => {
    if (relatedPage.value >= relatedLastPage.value || pendingRelated.value) return;

    pendingRelated.value = true;
    relatedPage.value++;

    try {
        const res = await $fetch(`${siteUrl}/api/articles/${slug}/related`, {
            query: { page: relatedPage.value, per_page: 4 }
        });

        if (res && res.data) {
            // Добавляем новые статьи к существующему массиву
            relatedArticles.value.push(...res.data);
        }
    } catch (error) {
        console.error('Ошибка при загрузке похожих статей:', error);
        relatedPage.value--; // Откатываем страницу назад в случае ошибки
    } finally {
        pendingRelated.value = false;
    }
};

// Функция для форматирования даты карточек (переиспользуемая)
const formatDateShort = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ru-RU', {
        day: 'numeric', month: 'long', year: 'numeric'
    });
};

// 6. Прогресс-бар
const readingProgress = ref('0%');
const updateScroll = () => {
    const scrollPx = document.documentElement.scrollTop;
    const winHeightPx = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    readingProgress.value = `${(scrollPx / winHeightPx) * 100}%`;
};

const viewsCount = ref(article.value?.views || 0);

onMounted(() => {
    // 1. Логика прогресс-бара скролла
    window.addEventListener('scroll', updateScroll);

    // 2. Умный счетчик просмотров: отправляем POST через 5 секунд чтения
    setTimeout(async () => {
        try {
            const res = await $fetch(`${siteUrl}/api/articles/${slug}/views`, {
                method: 'POST'
            });
            // Обновляем счетчик на лету без перезагрузки страницы
            if (res && res.views) {
                viewsCount.value = res.views;
            }
        } catch (e) {
            console.error('Ошибка обновления просмотров', e);
        }
    }, 5000); // 5000 мс = 5 секунд
});
onUnmounted(() => window.removeEventListener('scroll', updateScroll));
</script>

<template>
    <div class="min-h-screen bg-[#0F1113] text-gray-300 font-sans relative overflow-x-hidden">
        <div v-if="pending" class="flex flex-col items-center justify-center min-h-[60vh]">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
            <p class="text-gray-500 animate-pulse">Загрузка нейронных связей...</p>
        </div>

        <template v-else-if="article">

            <div class="fixed top-0 left-0 w-full h-1 bg-gray-800 z-50">
                <div class="h-full bg-gradient-to-r from-orange-600 to-orange-400 transition-all duration-150 ease-out" :style="{ width: readingProgress }"></div>
            </div>

            <header class="relative pt-24 pb-16 lg:pt-32 lg:pb-24 overflow-hidden border-b border-white/5">
                <div class="absolute inset-0 z-0">
                    <img :src="article.cover_image" class="w-full h-full object-cover opacity-20" :alt="article.title" />
                    <div class="absolute inset-0 bg-gradient-to-t from-[#0F1113] via-[#0F1113]/80 to-transparent"></div>
                </div>

                <div class="relative z-10 max-w-4xl mx-auto px-6 animate-fade-in-up">

                    <nav class="flex items-center gap-2 text-xs font-medium text-gray-500 mb-10" aria-label="Breadcrumb">
                        <NuxtLink to="/" class="hover:text-orange-500 transition-colors">Главная</NuxtLink>
                        <svg class="w-3 h-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                        <NuxtLink to="/blog" class="hover:text-orange-500 transition-colors">Блог</NuxtLink>
                        <template v-if="article.subcategory">
                            <svg class="w-3 h-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                            <NuxtLink :to="`/blog/${article.subcategory.category.slug}`" class="hover:text-orange-500 transition-colors">
                                {{ article.subcategory.category.name }}
                            </NuxtLink>
                        </template>
                        <template v-if="article.subcategory">
                            <svg class="w-3 h-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                            <NuxtLink :to="`/blog/${article.subcategory.category.slug}/${article.subcategory.slug}`" class="hover:text-orange-500 transition-colors">
                                {{ article.subcategory.name }}
                            </NuxtLink>
                        </template>
                    </nav>

                    <div class="flex flex-wrap items-center gap-4 text-xs font-bold text-gray-500 uppercase tracking-[0.15em] mb-6">
                        <span>{{ formattedDate }}</span>
                        <span class="w-1 h-1 rounded-full bg-gray-700"></span>
                        <span class="flex items-center gap-1.5">
                        <svg class="w-4 h-4 text-orange-500/50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        {{ article.read_time }} мин
                    </span>
                        <span class="w-1 h-1 rounded-full bg-gray-700"></span>
                        <span class="flex items-center gap-1.5">
                        <svg class="w-4 h-4 text-orange-500/50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        {{ viewsCount }}
                    </span>
                    </div>

                    <h1 class="text-4xl md:text-5xl lg:text-7xl font-black text-white leading-[1.1] mb-12 tracking-tight">
                        {{ article.title }}
                    </h1>

                    <div class="flex items-center gap-6 mb-10 group">
                        <div class="relative">
                            <div class="absolute -inset-1 bg-gradient-to-r from-orange-600 to-orange-400 rounded-full blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
                            <div class="relative w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-orange-500/30 overflow-hidden shadow-2xl">
                                <img
                                    src="https://gefestai.com/storage/bg/20260125_020833.jpg"
                                    alt="Дмитрий Повышев"
                                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                            </div>
                            <div class="absolute -bottom-1 -right-1 w-6 h-6 bg-orange-600 border-2 border-[#0F1113] rounded-full flex items-center justify-center shadow-lg">
                                <svg class="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                                </svg>
                            </div>
                        </div>

                        <div>
                            <p class="text-xl md:text-2xl text-white font-black tracking-tight mb-1">
                                Дмитрий Повышев
                            </p>
                            <p class="text-xs md:text-sm text-orange-500 font-bold uppercase tracking-[0.2em] opacity-90">
                                Основатель Gefest AI
                            </p>
                        </div>
                    </div>
                    <div class="flex flex-wrap gap-2 pt-4 border-t border-white/5">
                        <NuxtLink
                            v-for="tag in mockTags"
                            :key="tag"
                            :to="`/blog/tag/${tag.toLowerCase()}`"
                            class="px-3 py-1.5 rounded-lg bg-[#1A1C1E] border border-white/5 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-orange-500 hover:border-orange-500/30 transition-all duration-300"
                        >
                            #{{ tag }}
                        </NuxtLink>
                    </div>

                </div>
            </header>

            <main class="max-w-3xl mx-auto px-6 py-12 lg:py-16">

                <div v-if="article.ai_summary" class="bg-gradient-to-br from-[#1A1C1E] to-[#131416] border border-orange-500/20 rounded-2xl p-6 md:p-8 mb-12 shadow-[0_0_30px_-10px_rgba(234,88,12,0.15)] relative overflow-hidden group animate-fade-in-up" style="animation-delay: 0.1s;">
                    <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-600 to-transparent"></div>
                    <div class="flex items-start gap-4">
                        <div class="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                            <svg class="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        </div>
                        <div>
                            <h3 class="text-sm font-bold text-white uppercase tracking-wider mb-3">О чем статья</h3>
                            <p class="text-gray-300 leading-relaxed text-sm md:text-base whitespace-pre-line">
                                {{ article.ai_summary }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="custom-article-styles animate-fade-in-up" style="animation-delay: 0.2s;">
                    <template v-for="(block, index) in articleBlocks" :key="index">

                        <div
                            v-if="block.type === 'text'"
                            v-html="block.data"
                            class="prose prose-invert prose-lg max-w-none text-gray-300 leading-relaxed marker:text-orange-500 prose-headings:text-white prose-a:text-orange-400 hover:prose-a:text-orange-300"
                        ></div>

                        <figure v-else-if="block.type === 'image'" class="my-14 lg:my-20 group relative rounded-3xl overflow-hidden bg-[#1A1C1E] border border-white/5 shadow-2xl mx-[-1rem] sm:mx-0">
                            <img
                                :src="block.data.url"
                                :alt="block.data.alt"
                                class="w-full h-auto object-cover transform group-hover:scale-[1.03] transition-transform duration-[1.5s] ease-out"
                            />
                            <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                            <figcaption v-if="block.data.caption" class="absolute bottom-0 left-0 w-full p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500 backdrop-blur-sm bg-black/20">
                                <span class="text-orange-500 text-[10px] uppercase tracking-[0.3em] block mb-2 font-black">Gefest Vision System</span>
                                <p class="text-sm text-white font-medium leading-snug">
                                    {{ block.data.caption }}
                                </p>
                            </figcaption>
                        </figure>

                    </template>
                </div>

<!--                <div class="mt-16 pt-8 border-t border-white/5 animate-fade-in-up" style="animation-delay: 0.3s;">-->
<!--                    <div class="flex flex-wrap gap-3">-->
<!--                        <NuxtLink-->
<!--                            v-for="tag in mockTags"-->
<!--                            :key="tag"-->
<!--                            :to="`/blog/tag/${tag.toLowerCase()}`"-->
<!--                            class="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-gray-400 hover:text-white hover:border-orange-500/50 hover:bg-orange-500/10 transition-all cursor-pointer"-->
<!--                        >-->
<!--                            #{{ tag }}-->
<!--                        </NuxtLink>-->
<!--                    </div>-->
<!--                </div>-->

                <div class="mt-16 p-8 rounded-3xl bg-[#131416]/80 backdrop-blur-md border border-white/5 flex flex-col md:flex-row items-center justify-between gap-6 animate-fade-in-up shadow-xl" style="animation-delay: 0.4s;">
                    <div class="text-center md:text-left">
                        <h4 class="text-lg font-bold text-white mb-2">Понравился материал?</h4>
                        <p class="text-sm text-gray-400 max-w-sm">
                            Присоединяйтесь к комьюнити Gefest AI. Мы делимся инсайтами про вайбкодинг, ИИ и автоматизацию.
                        </p>
                    </div>
                    <div class="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                        <a href="https://t.me/gefestai" target="_blank" class="px-6 py-3 rounded-xl bg-orange-600 hover:bg-orange-500 text-white font-bold transition-all shadow-[0_0_20px_-5px_rgba(234,88,12,0.4)] flex items-center justify-center gap-2 group whitespace-nowrap">
                            <svg class="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.19-.08-.05-.19-.02-.27 0-.11.03-1.84 1.16-5.18 3.42-.49.33-.94.5-1.35.49-.45-.01-1.3-.25-1.94-.46-.78-.26-1.4-.4-1.35-.85.03-.24.36-.48.99-.74 3.88-1.69 6.47-2.8 7.77-3.34 3.69-1.54 4.46-1.81 4.96-1.82.11 0 .35.03.48.14.11.09.14.22.15.33-.01.07-.01.16-.02.21z"/></svg>
                            Подписаться в Telegram
                        </a>
                    </div>
                </div>

            </main>

            <section class="w-full bg-[#0A0C0E] border-t border-white/5 py-16 lg:py-24">
                <div class="max-w-[1400px] mx-auto px-6">
                    <div class="flex flex-col md:flex-row justify-between items-end mb-10 gap-4">
                        <div>
                            <h2 class="text-3xl font-bold text-white mb-2">Продолжить чтение</h2>
                            <p class="text-gray-500">Материалы, которые дополнят картину</p>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <NuxtLink
                            v-for="item in relatedArticles"
                            :key="item.id"
                            :to="`/blog/${item.subcategory.category.slug}/${item.subcategory.slug}/${item.slug}`"
                            class="group flex flex-col bg-[#131416] rounded-2xl overflow-hidden border border-white/5 hover:border-orange-500/30 transition-all duration-300"
                        >
                            <div class="relative h-48 overflow-hidden">
                                <img :src="'/storage/'+item.cover_image" :alt="item.title" class="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700">
                                <div class="absolute inset-0 bg-gradient-to-t from-[#131416] to-transparent opacity-80"></div>
                            </div>
                            <div class="p-6 flex-grow flex flex-col justify-between">
                                <div>
                        <span class="text-xs font-bold text-orange-500 mb-3 block uppercase tracking-wider">
                            {{ formatDateShort(item.published_at) }}
                        </span>
                                    <h3 class="text-lg font-bold text-white leading-snug group-hover:text-orange-400 transition-colors">
                                        {{ item.title }}
                                    </h3>
                                </div>
                            </div>
                        </NuxtLink>
                    </div>

                    <div v-if="relatedPage < relatedLastPage" class="mt-12 text-center">
                        <button
                            @click="loadMoreRelated"
                            :disabled="pendingRelated"
                            class="px-8 py-3 rounded-full bg-transparent border border-white/20 text-white font-medium hover:bg-white/5 hover:border-white/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto gap-2"
                        >
                            <span v-if="pendingRelated" class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                            {{ pendingRelated ? 'Загрузка...' : 'Показать еще' }}
                        </button>
                    </div>
                </div>
            </section>

        </template>
    </div>
</template>

<style>
/* Стили для v-html остались теми же */
.custom-article-styles h2 {
    @apply text-2xl md:text-3xl font-bold mt-16 mb-6 tracking-tight text-white border-b border-white/5 pb-4;
}
.custom-article-styles h3 {
    @apply text-xl md:text-2xl font-bold mt-10 mb-4 text-gray-100;
}
.custom-article-styles p {
    @apply mb-6 leading-relaxed;
}
.custom-article-styles ul {
    @apply list-disc list-outside ml-6 mb-8 space-y-3;
}
.custom-article-styles li {
    @apply pl-2;
}
.custom-article-styles li strong {
    @apply text-orange-400 font-semibold;
}
.custom-article-styles blockquote {
    @apply pl-6 border-l-4 border-orange-500 italic text-xl md:text-2xl text-gray-400 my-10 py-4 pr-6 bg-gradient-to-r from-orange-500/5 to-transparent rounded-r-2xl;
}
.custom-article-styles blockquote strong {
    @apply text-orange-400 font-medium text-lg block mt-4 not-italic;
}
</style>

<style scoped>
@keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in-up {
    animation: fadeInUp 0.6s ease-out forwards;
    opacity: 0;
}
</style>
